/**
 * Responsive image component using <picture> with WebP + JPG fallback.
 * Source files expected at /img/{slug}-{sm|md|lg}.{webp|jpg}
 *
 * Sizes: sm=400px, md=800px, lg=1200px (auto-served via srcset).
 *
 * Performance:
 *   - Lazy-loaded by default (loading="lazy") except hero images (use `priority` prop).
 *   - `decoding="async"` everywhere.
 *   - WebP preferred via <source type="image/webp">.
 *
 * SEO:
 *   - `alt` is required (typed) and indexed by Google Image Search.
 *   - Falls back to JPG for browsers without WebP support (none in 2026 but kept for safety).
 */

import { IMAGES } from "../data/images";

const SIZES = "sm md lg";

export default function ResponsiveImage({
    slug,
    alt: altOverride,
    className = "",
    priority = false,
    sizes = "(max-width: 768px) 400px, (max-width: 1200px) 800px, 1200px",
    width,
    height,
    ratioClass = "",
    ...rest
}) {
    const meta = IMAGES[slug] || {};
    const alt = altOverride || meta.alt || "Réparation électroménager Electrocase";
    const loading = priority ? "eager" : "lazy";
    const fetchPriority = priority ? "high" : "auto";

    const webpSrcSet = `/img/${slug}-sm.webp 400w, /img/${slug}-md.webp 800w, /img/${slug}-lg.webp 1200w`;
    const jpgSrcSet = `/img/${slug}-sm.jpg 400w, /img/${slug}-md.jpg 800w, /img/${slug}-lg.jpg 1200w`;

    // Use the smallest variant as the fallback <img src> so mobile LCP downloads
    // ~25KB (sm) instead of ~100KB (md) when a browser is slow to evaluate <source>.
    const fallbackSrc = `/img/${slug}-sm.jpg`;

    return (
        <picture data-testid={`img-${slug}`} data-img-sizes={SIZES}>
            <source type="image/webp" srcSet={webpSrcSet} sizes={sizes} />
            <source type="image/jpeg" srcSet={jpgSrcSet} sizes={sizes} />
            <img
                src={fallbackSrc}
                alt={alt}
                width={width}
                height={height}
                loading={loading}
                fetchPriority={fetchPriority}
                decoding="async"
                className={`${className} ${ratioClass}`}
                {...rest}
            />
        </picture>
    );
}
